import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { authenticatedAPICall } from '../../client/api-client';
import {
  PickupOrderStatusBeingLoaded,
  PickupOrderStatusLoadingDone,
  PickupOrderStatusBooked,
  PickupOrderStatusPackageReceiptCreated,
  PickupOrderStatusPackageReceiptBeingSend,
  PickupOrderStatusPackageReceiptSent,
  PickupOrderStatusManualActionRequired,
  formatDateInput,
  formatDate,
} from '../../util/util';
import '../../elements/pickup-order/pickup-order';

import { PickupOrder, PickupSubOrder, Product } from '../../types/types';

type PickupOrderList = PickupOrder[];

@customElement('langhout-beton-order-loading-done')
export class LanghoutBetonOrderSetupDone extends LitElement {
  static styles = css`
    input {
      padding: 7px;
      border-radius: 5px;
      font-size: 16px;
    }
  `;

  private limit = 100;
  private offset = 0;

  @property({ type: Array })
  private pickupOrders: PickupOrderList = [];

  @query('#showDate')
  public showDate?: HTMLInputElement;

  private currentDate = new Date();

  constructor() {
    super();
    this.listPickupOrders();
    document.addEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'refresh-tasks',
      this.refreshTaskListener as EventListener,
    );
  }

  refreshTaskListener = () => {
    this.listPickupOrders();
  };

  render() {
    if (this.pickupOrders.length === 0) {
      return html`
        <input
          type="date"
          id="showDate"
          name="showDate"
          value="${formatDateInput(this.currentDate.toISOString())}"
          @change="${() => {
            this.listPickupOrders();
          }}"
        />
        <h2>
          Er zijn geen afgeronde orders op
          ${formatDate(this.currentDate.toISOString())}
        </h2>
      `;
    }

    return html`
      <input
        type="date"
        id="showDate"
        name="showDate"
        value="${formatDateInput(this.currentDate.toISOString())}"
        @change="${() => {
          this.listPickupOrders();
        }}"
      />
      <h2>Afgerond op ${formatDate(this.currentDate.toISOString())}</h2>
      <h3>Tonnage: ${this.getTotalWeight()}</h3>
      ${repeat(
        this.pickupOrders,
        pickupOrder => pickupOrder.id,
        pickupOrder => {
          return html`
            <pickup-order
              displayOnly
              displayCommentButton
              .pickupOrder="${pickupOrder}"
            ></pickup-order>
          `;
        },
      )}
    `;
  }

  getTotalWeight = () => {
    let totalWeight = 0;
    this.pickupOrders.forEach((pickupOrder: PickupOrder) => {
      pickupOrder.sub_orders.forEach((p: PickupSubOrder) => {
        p.products.forEach((p: Product) => {
          totalWeight += p.amount * p.weight;
        });
      });
    });
    totalWeight = Math.round(totalWeight);

    return `${totalWeight.toLocaleString('nl-NL')} kg`;
  };

  listPickupOrders = async () => {
    let showDoneFromDate: string = formatDate(new Date().toISOString());
    const showDate = this.showDate ? this.showDate.value : '';
    if (showDate != '') {
      this.currentDate = new Date(showDate);
      showDoneFromDate = formatDate(showDate);
    }
    if (this.currentDate) {
      showDoneFromDate = formatDate(this.currentDate.toISOString());
    }
    const path = `/pickuporders/tasks?limit=${this.limit}&order_by=being_loaded_time&being_loaded_date=${showDoneFromDate}&offset=${this.offset}&states=${PickupOrderStatusBeingLoaded},${PickupOrderStatusLoadingDone},${PickupOrderStatusBooked},${PickupOrderStatusPackageReceiptCreated},${PickupOrderStatusPackageReceiptBeingSend},${PickupOrderStatusPackageReceiptSent},${PickupOrderStatusManualActionRequired}&view=true`;

    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (result.statusCode !== undefined && result.statusCode >= 200 && result.statusCode < 300 && Array.isArray(result.body)) {
      this.pickupOrders = result.body as PickupOrderList;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-order-loading-done': LanghoutBetonOrderSetupDone;
  }
}
